import { useState, useEffect, useRef } from "react";
import { sqlReadRaw as sqlRead, turnoverWrite as sqlWrite, payloadOf } from "../utils/sqlClient";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";
import {
  savedConfigId, rowKeyOf, withKeys, isBlank, upsertRow, nextDisplayOrder, contactPayload,
} from "./turnover/configContacts";
import DropdownListsPanel from "./turnover/DropdownListsPanel";

// Turnover Configuration — standing contact defaults used across the turnover
// workflow, editable here instead of hardcoded in source. Backed by
// TurnoverConfigContacts (db/28_turnover_config.sql).
const CONTRACT_REVIEW_ROLES = [
  "Accounting", "Legal / Executive", "Construction", "Procurement",
  "Engineering", "Quality", "Safety", "COO / Executive",
];

const rowStyle = { display: "grid", gridTemplateColumns: "180px 1fr 1fr 30px", gap: 8, alignItems: "center", marginBottom: 6 };
const ctl = { padding: "6px 9px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: "100%", boxSizing: "border-box" };

// Defined at module level (not inside the page) so a save that updates the
// list doesn't remount every input and drop what the admin is typing.
function NameEmailRow({ contact, onCommit, onRemove }) {
  return (
    <div style={rowStyle}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text2)" }}>{contact.RoleLabel}</div>
      <input
        defaultValue={contact.Name || ""}
        placeholder="Name"
        onBlur={e => { if ((e.target.value || "") !== (contact.Name || "")) onCommit({ Name: e.target.value }); }}
        style={ctl}
      />
      <input
        defaultValue={contact.Email || ""}
        placeholder="email@cleanspaceus.com"
        onBlur={e => { if ((e.target.value || "") !== (contact.Email || "")) onCommit({ Email: e.target.value.trim() }); }}
        style={ctl}
      />
      {onRemove ? (
        <button
          onClick={onRemove}
          title="Remove"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db" }}
          onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
          onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
        >🗑</button>
      ) : <span />}
    </div>
  );
}

export default function TurnoverConfig() {
  const isAdmin = useIsAdmin();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  // Latest values + in-flight save per row key: a row's saves run one after
  // another, so the second one carries the ConfigID the first returned
  // instead of inserting a duplicate row.
  const rowsRef  = useRef(new Map());
  const queueRef = useRef(new Map());
  const draftSeq = useRef(0);

  const load = () => {
    setLoading(true);
    setError("");
    sqlRead("EXEC sp_GetTurnoverConfigContacts")
      .then(result => {
        const rows = withKeys(result?.Table1 || []);
        rowsRef.current = new Map(rows.map(c => [c._key, c]));
        setContacts(rows);
      })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  function updateContact(contact, patch) {
    const key = rowKeyOf(contact);
    const merged = { ...(rowsRef.current.get(key) || contact), ...patch, _key: key };
    rowsRef.current.set(key, merged);
    setContacts(prev => upsertRow(prev, key, patch, contact));
    if (!merged.ConfigID && isBlank(merged)) return; // nothing worth storing yet

    const run = async () => {
      const current = rowsRef.current.get(key);
      if (!current) return; // removed while queued
      const result = await sqlWrite(`EXEC sp_SaveTurnoverConfigContact @payload = '${payloadOf(contactPayload(current))}'`);
      const id = savedConfigId(result);
      if (!current.ConfigID) {
        if (!id) throw new Error("The contact was not saved (no id returned).");
        rowsRef.current.set(key, { ...rowsRef.current.get(key), ConfigID: id });
        setContacts(prev => prev.map(c => (c._key === key ? { ...c, ConfigID: id } : c)));
      }
    };
    const next = (queueRef.current.get(key) || Promise.resolve()).then(run).catch(e => {
      alert("Error saving: " + e.message);
      load();
    });
    queueRef.current.set(key, next);
  }

  // "+ Add CC" adds a local row; it is stored on its first non-blank edit.
  function addContact(groupKey, roleLabel) {
    const key = `draft-${groupKey}-${++draftSeq.current}`;
    const draft = { ConfigID: null, GroupKey: groupKey, RoleLabel: roleLabel, Name: "", Email: "", DisplayOrder: nextDisplayOrder(contacts, groupKey), _key: key };
    rowsRef.current.set(key, draft);
    setContacts(prev => [...prev, draft]);
  }

  async function removeContact(contact) {
    const key = rowKeyOf(contact);
    await (queueRef.current.get(key) || Promise.resolve());
    const current = rowsRef.current.get(key) || contact;
    if (current.ConfigID && !window.confirm(`Remove ${current.Name || current.Email || "this recipient"}?`)) return;
    rowsRef.current.delete(key);
    setContacts(prev => prev.filter(c => c._key !== key));
    if (!current.ConfigID) return;
    try {
      await sqlWrite(`EXEC sp_DeleteTurnoverConfigContact @payload = '${payloadOf({ config_id: current.ConfigID })}'`);
    } catch (e) {
      alert("Error removing: " + e.message);
      load();
    }
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;

  if (loading) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;

  const placeholder = (groupKey, roleLabel, displayOrder) =>
    ({ ConfigID: null, GroupKey: groupKey, RoleLabel: roleLabel, Name: "", Email: "", DisplayOrder: displayOrder });
  const findOne = (groupKey, roleLabel, displayOrder) =>
    contacts.find(c => c.GroupKey === groupKey && c.RoleLabel === roleLabel)
    || { ...placeholder(groupKey, roleLabel, displayOrder), _key: rowKeyOf(placeholder(groupKey, roleLabel, displayOrder)) };

  const contractReviewRows = CONTRACT_REVIEW_ROLES.map(role => findOne("contract_review", role, 100));
  const jobNumberTo = findOne("job_number_alert", "To", 10);
  const jobNumberCC = contacts.filter(c => c.GroupKey === "job_number_alert" && c.RoleLabel === "CC");
  const packageStanding = contacts.filter(c => c.GroupKey === "package_email" && c.RoleLabel === "Standing");
  const pmAlertTo = findOne("pm_assignment_alert", "To", 10);
  const pmAlertCC = contacts.filter(c => c.GroupKey === "pm_assignment_alert" && c.RoleLabel === "CC");
  const marketingDirector = findOne("marketing_team", "Director", 5);
  const marketingTeam = contacts.filter(c => c.GroupKey === "marketing_team" && c.RoleLabel === "Member");

  const addBtn = { marginTop: 4, padding: "5px 12px", fontSize: 12, fontWeight: 700, background: "var(--surface)", color: "var(--cs-blue)", border: "1px dashed var(--cs-blue)", borderRadius: 6, cursor: "pointer" };
  const row = (c, removable) => (
    <NameEmailRow key={c._key} contact={c} onCommit={patch => updateContact(c, patch)} onRemove={removable ? () => removeContact(c) : null} />
  );

  return (
    <div style={{ padding: 32, display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap" }}>
    <div style={{ flex: "1 1 560px", maxWidth: 780 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Turnover Configuration</h2>
      <p style={{ margin: "0 0 28px", fontSize: 13, color: "var(--text3)" }}>
        Standing contact defaults used across the turnover workflow — edit here instead of source code. Each field saves when you click out of it.
      </p>

      <section style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Contract Review Defaults</h3>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
          Default reviewer for each fixed sign-off role, seeded into a new turnover's Contract Review matrix
          (Section 2). Sales and Estimating are populated from the turnover itself and aren't listed here.
        </p>
        {contractReviewRows.map(c => row(c, false))}
      </section>

      <section style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Job Number Assignment Alert</h3>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
          Sent to Accounting when a turnover is submitted for a job number. The same list receives the Job Number Assigned email.
        </p>
        {row(jobNumberTo, false)}
        {jobNumberCC.map(c => row(c, true))}
        <button onClick={() => addContact("job_number_alert", "CC")} style={addBtn}>+ Add CC</button>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Project Manager Assignment Alert</h3>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
          Sent with the project description when a turnover is submitted for a job number, so the VP of Construction
          can assign the Project Manager. Nothing is sent while the To row has no email.
        </p>
        {row(pmAlertTo, false)}
        {pmAlertCC.map(c => row(c, true))}
        <button onClick={() => addContact("pm_assignment_alert", "CC")} style={addBtn}>+ Add CC</button>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Marketing Team</h3>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
          Can edit a turnover&rsquo;s §18 Marketing (along with that turnover&rsquo;s sales lead and Project Manager) and are
          invited to the turnover meeting. The <strong>Director</strong> owns §18 by default and is copied on the Job Number Assigned
          email to coordinate the Marketing section with the sales lead and the PM.
        </p>
        {row(marketingDirector, false)}
        {marketingTeam.map(c => row(c, true))}
        <button onClick={() => addContact("marketing_team", "Member")} style={addBtn}>+ Add Member</button>
      </section>

      <section>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Turnover Package — Standing Recipients</h3>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
          Pre-checked on the Turnover Package&rsquo;s &ldquo;Email Package&rdquo; panel, alongside that turnover&rsquo;s
          own Contract Review roster.
        </p>
        {packageStanding.map(c => row(c, true))}
        <button onClick={() => addContact("package_email", "Standing")} style={addBtn}>+ Add Recipient</button>
      </section>
    </div>
    <aside style={{ flex: "0 1 380px", minWidth: 300, paddingTop: 44 }}>
      <DropdownListsPanel />
    </aside>
    </div>
  );
}
